import { useState } from "react";
import { Button, Card } from "@insights/ui";
import { Check, Copy, ExternalLink, KeyRound, Terminal } from "lucide-react";
import { Link, useParams } from "react-router";
import { PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import {
  buildLoginSnippet,
  buildWorkspaceSnippet,
} from "../helpers/build-terrabuild-snippets";
import "../styles/terrabuild-integration.css";

type SnippetName = "login" | "workspace";

function Snippet({
  label,
  name,
  value,
  copied,
  onCopy,
}: {
  label: string;
  name: SnippetName;
  value: string;
  copied: SnippetName | undefined;
  onCopy: (name: SnippetName, value: string) => Promise<void>;
}) {
  const isCopied = copied === name;

  return (
    <div className="integration-snippet">
      <pre>
        <code>{value}</code>
      </pre>
      <div className="integration-copy">
        <Button
          type="button"
          aria-label={`Copy ${label}`}
          icon={isCopied ? <Check /> : <Copy />}
          onClick={() => onCopy(name, value)}
          size="small"
        >
          {isCopied ? "Copied" : "Copy"}
        </Button>
      </div>
    </div>
  );
}

export function TerrabuildIntegrationPage() {
  const { workspaceId = "" } = useParams();
  const [copied, setCopied] = useState<SnippetName>();
  const workspaceSnippet = buildWorkspaceSnippet(workspaceId);
  const loginSnippet = buildLoginSnippet(workspaceId);

  async function copySnippet(name: SnippetName, value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(name);
    window.setTimeout(() => {
      setCopied((current) => (current === name ? undefined : current));
    }, 2000);
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Integration" title="Terrabuild" />
      <div className="integration-layout">
        <Card className="integration-card integration-introduction">
          <Terminal />
          <div>
            <h2>Connect this workspace</h2>
            <p>
              Associate your repository with this Insights workspace so
              Terrabuild can publish build and artifact data to it.
            </p>
          </div>
        </Card>

        <Card className="integration-card">
          <span className="integration-step">Workspace identity</span>
          <h2>Your workspace ID</h2>
          <p>
            Terrabuild uses this identifier to select the Insights workspace
            receiving build data.
          </p>
          <code className="integration-workspace-id">{workspaceId}</code>
        </Card>

        <Card className="integration-card">
          <span className="integration-step">Step 1</span>
          <h2>Configure the WORKSPACE file</h2>
          <p>
            Add this block to the repository&apos;s <code>WORKSPACE</code> file.
          </p>
          <Snippet
            label="WORKSPACE configuration"
            name="workspace"
            value={workspaceSnippet}
            copied={copied}
            onCopy={copySnippet}
          />
          <a
            className="integration-doc-link"
            href="https://terrabuild.io/docs/syntax/workspace/workspace/#example-usage"
            target="_blank"
            rel="noreferrer"
          >
            WORKSPACE syntax documentation
            <ExternalLink />
          </a>
        </Card>

        <Card className="integration-card">
          <span className="integration-step">Step 2</span>
          <h2>Create a contributor token</h2>
          <p>
            Generate a workspace-scoped contributor token from Security. Copy
            it when it is created and store it as a protected CI secret.
          </p>
          <Link
            className="integration-action-link"
            to={`/w/${workspaceId}/security`}
          >
            <KeyRound />
            Open Security
          </Link>
        </Card>

        <Card className="integration-card">
          <span className="integration-step">Step 3</span>
          <h2>Connect Terrabuild</h2>
          <p>
            Authenticate Terrabuild with the token and master key stored by
            your CI system.
          </p>
          <Snippet
            label="Terrabuild login command"
            name="login"
            value={loginSnippet}
            copied={copied}
            onCopy={copySnippet}
          />
          <a
            className="integration-doc-link"
            href="https://terrabuild.io/docs/usage/#connect-to-a-shared-cache"
            target="_blank"
            rel="noreferrer"
          >
            Terrabuild connection documentation
            <ExternalLink />
          </a>
        </Card>
      </div>
    </AppShell>
  );
}
